import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api, { getError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Loader2, Upload } from "lucide-react";

const emptyForm = { name: "", employee_number: "", job_title: "", department: "" };

export default function Employees() {
  const { can } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/employees", { params: search ? { search } : {} });
      setEmployees(data);
    } catch (e) {
      toast.error(getError(e, "تعذر تحميل الموظفين"));
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({ name: emp.name, employee_number: emp.employee_number, job_title: emp.job_title || "", department: emp.department || "" });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/employees/${editing.id}`, form);
        toast.success("تم تعديل الموظف بنجاح");
      } else {
        await api.post("/employees", form);
        toast.success("تمت إضافة الموظف بنجاح");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(getError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (emp) => {
    if (!window.confirm(`هل أنت متأكد من حذف الموظف ${emp.name}؟`)) return;
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("تم حذف الموظف");
      load();
    } catch (err) {
      toast.error(getError(err));
    }
  };

  const importExcel = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/employees/import", fd);
      toast.success(`تم استيراد ${data.inserted} موظف بنجاح`);
      load();
    } catch (err) {
      toast.error(getError(err, "تعذر استيراد الملف"));
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="employees-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl md:text-3xl font-extrabold text-primary">الموظفون</h1>
        {can("add") && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-secondary/40 text-secondary hover:bg-secondary hover:text-white transition-colors" data-testid="import-excel-btn">
              {importing ? <Loader2 className="h-4 w-4 animate-spin ms-1" /> : <Upload className="h-4 w-4 ms-1" />}
              استيراد من اكسل
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={importExcel} data-testid="import-excel-input" />
            <Button onClick={openAdd} className="bg-primary hover:bg-secondary transition-colors" data-testid="add-employee-btn">
              <Plus className="h-4 w-4 ms-1" />
              إضافة موظف
            </Button>
          </div>
        )}
      </div>

      <div className="bg-white rounded-lg border border-border p-4">
        <Input placeholder="بحث في بيانات الموظفين..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="employees-search-input" />
      </div>

      <div className="bg-white rounded-lg border border-border overflow-x-auto">
        <table className="w-full text-sm" data-testid="employees-table">
          <thead>
            <tr className="bg-primary text-white">
              <th className="px-3 py-3 text-right font-bold">م</th>
              <th className="px-3 py-3 text-right font-bold">اسم الموظف</th>
              <th className="px-3 py-3 text-right font-bold">الرقم الوظيفي</th>
              <th className="px-3 py-3 text-right font-bold">المسمى الوظيفي</th>
              <th className="px-3 py-3 text-right font-bold">القسم/الإدارة</th>
              <th className="px-3 py-3 text-right font-bold">إجراءات</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="text-center py-10"><Loader2 className="h-6 w-6 animate-spin mx-auto text-primary" /></td></tr>
            ) : employees.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-10 text-muted-foreground" data-testid="employees-empty">لا يوجد موظفون مسجلون</td></tr>
            ) : (
              employees.map((emp, i) => (
                <tr key={emp.id} className="border-t border-border hover:bg-muted/50 transition-colors" data-testid={`employee-row-${i}`}>
                  <td className="px-3 py-3">{i + 1}</td>
                  <td className="px-3 py-3 font-bold text-primary">{emp.name}</td>
                  <td className="px-3 py-3">{emp.employee_number}</td>
                  <td className="px-3 py-3">{emp.job_title}</td>
                  <td className="px-3 py-3">{emp.department}</td>
                  <td className="px-3 py-3">
                    <div className="flex gap-1">
                      {can("edit") && (
                        <Button variant="ghost" size="sm" onClick={() => openEdit(emp)} className="text-secondary" title="تعديل" data-testid={`edit-employee-btn-${i}`}>
                          <Pencil className="h-4 w-4" />
                        </Button>
                      )}
                      {can("delete") && (
                        <Button variant="ghost" size="sm" onClick={() => remove(emp)} className="text-red-600" title="حذف" data-testid={`delete-employee-btn-${i}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg" dir="rtl" data-testid="employee-form-dialog">
          <DialogHeader>
            <DialogTitle className="text-primary">{editing ? "تعديل موظف" : "إضافة موظف"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="space-y-2">
              <Label>اسم الموظف</Label>
              <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="employee-name-input" />
            </div>
            <div className="space-y-2">
              <Label>الرقم الوظيفي</Label>
              <Input required value={form.employee_number} onChange={(e) => setForm({ ...form, employee_number: e.target.value })} data-testid="employee-number-input" />
            </div>
            <div className="space-y-2">
              <Label>المسمى الوظيفي</Label>
              <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} data-testid="employee-title-input" />
            </div>
            <div className="space-y-2">
              <Label>القسم/الإدارة</Label>
              <Input value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })} data-testid="employee-department-input" />
            </div>
            <DialogFooter>
              <Button type="submit" disabled={saving} className="bg-primary hover:bg-secondary transition-colors" data-testid="employee-save-btn">
                {saving && <Loader2 className="h-4 w-4 animate-spin ms-2" />}
                {editing ? "حفظ التعديلات" : "إضافة الموظف"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
